import type {
  EntityReconcileCandidate,
  EntityReconcileScoredResponse,
  EntityResponse,
} from 'types/api-responses';
import type { ConsolidationCandidate } from './consolidation-utils';
import { flaggedAddressRows } from 'pages/documents/structuring/reconcile/candidate-utils';
import type { ReconcileCandidate } from 'pages/documents/structuring/reconcile/types';
import { SUGGESTION_PREFIX } from 'pages/documents/structuring/utils';
import type { EntityType as EntityListType } from 'lib/entities';
import type { EntitySearchResult } from 'pages/entities/list/types';
import type { EntityCanonizeResponse } from './atoms';

// The list-facing entity-type label → concrete entity type. Shared by both
// reconcile tearsheets (was duplicated in each).
export const LABEL_TO_CONCRETE: Record<
  EntityListType,
  ReconcileCandidate['type']
> = {
  Individual: 'IndividualEntity',
  Organization: 'OrganizationEntity',
  Imported: 'ImportedEntity',
};

/**
 * An entity chosen from the Add Entities library search becomes a "User Added"
 * candidate with no agent score (§3.9). Its addresses aren't on the search
 * result's typed shape, so the primary-address picker starts empty for it.
 *
 * @param result - The library search result.
 */
export const userAddedCandidate = (
  result: EntitySearchResult
): ReconcileCandidate => ({
  id: result.id,
  name: result.name,
  type: LABEL_TO_CONCRETE[result.entityType],
  operator: result.operator ?? false,
  address: null,
  isStaged: false,
  sourceLabel: 'User Added',
  confidence: null,
  phone: null,
  recommended: false,
  flagged: false,
  flagCategory: result.flagCategory ?? null,
  flagRationale: result.flagRationale ?? null,
  flagSeverity: result.flagSeverity ?? null,
  addresses: [],
  addressFlagged: false,
});

/**
 * The canonical an "Add as New" creates, as a User Added candidate row.
 * @param {EntityCanonizeResponse} response The canonize endpoint's response.
 * @return {ReconcileCandidate} The canonical's row.
 */
export const canonizedCandidate = (
  response: EntityCanonizeResponse
): ReconcileCandidate => ({
  id: response.canonicalEntityId,
  name: response.name,
  type: response.entityType,
  operator: response.operator,
  address: null,
  isStaged: false,
  sourceLabel: 'User Added - Add as New',
  confidence: null,
  phone: null,
  recommended: false,
  flagged: false,
  addresses: [],
  addressFlagged: false,
});

/**
 * One line for an EntityResponse address (the scored endpoint pre-labels
 * candidate addresses; only the viewed entity needs assembling here).
 *
 * @param address - The address parts to join.
 */
const addressLabel = (address: {
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}) =>
  [address.streetAddress, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(', ');

/**
 * The viewed entity as the anchor candidate row: pinned first (isStaged drives
 * the table's pinning + the tile's anchor treatment), no confidence score.
 *
 * @param entity - The viewed entity.
 * @return The anchor candidate row.
 */
export const viewedEntityCandidate = (
  entity: EntityResponse
): ReconcileCandidate => {
  const primaryAddress =
    entity.addresses.find((a) => a.primary) ?? entity.addresses[0];
  const primaryPhone =
    entity.phoneNumbers.find((p) => p.primary) ?? entity.phoneNumbers[0];
  return {
    id: entity.id,
    name: entity.name,
    type: entity.type,
    operator: entity.operator ?? false,
    address: primaryAddress ? addressLabel(primaryAddress) : null,
    isStaged: true,
    sourceLabel: 'Viewed Entity',
    confidence: null,
    phone: primaryPhone?.number ?? null,
    recommended: false,
    flagged: false,
    flagCategory: entity.flagCategory,
    flagRationale: entity.flagRationale,
    flagSeverity: entity.flagSeverity,
    // The viewed entity's own unresolved address review flag, so the anchor row is
    // marked (and gates submission) the same as a flagged suggestion.
    addressFlagged: entity.addressFlagged,
    addressFlagCriticality: entity.addressFlagCriticality,
    addresses: flaggedAddressRows(
      entity.addresses.map((a) => ({
        id: a.id,
        label: addressLabel(a),
        criticality:
          entity.flaggedAddresses.find((f) => f.id === a.id)?.criticality ??
          null,
      }))
    ),
    titleWorkspaces: entity.titleWorkspaces,
    interpretationCount: entity.interpretationCount,
  };
};

/**
 * The row a scored candidate shows as: an alias group's display member, else
 * the candidate itself.
 *
 * @param candidate - The scored candidate or collapsed alias group.
 * @return The row's entity id.
 */
export const candidateRowId = (
  candidate: Pick<EntityReconcileCandidate, 'aliasGroup' | 'entityId'>
): number => candidate.aliasGroup?.displayEntityId ?? candidate.entityId;

/**
 * A scored candidate (or collapsed alias group) as a candidate row. Alias groups
 * surface as their display-name member — selecting the row submits that member's
 * id and transitive closure covers the family (Settled Decision #7); the row's
 * alias peers/pill render live from useAliasClusterStates.
 *
 * @param candidate - The scored candidate or collapsed alias group.
 * @param index - Its position in the scored list (0 is the top match).
 */
export const scoredCandidate = (
  candidate: EntityReconcileCandidate,
  index: number
): ReconcileCandidate => ({
  id: candidateRowId(candidate),
  name: candidate.aliasGroup?.displayName ?? candidate.name,
  type: candidate.entityType,
  operator: candidate.operator,
  address: candidate.address,
  isStaged: false,
  sourceLabel: candidate.aliasGroup
    ? `${SUGGESTION_PREFIX} ${index + 1} · Alias Group (${
        candidate.aliasGroup.memberCount
      } members)`
    : `${SUGGESTION_PREFIX} ${index + 1}`,
  confidence: candidate.confidence,
  phone: null,
  // The top match wears the "Recommended" badge (mock: first AI suggestion).
  recommended: index === 0,
  flagged: false,
  // Consolidation-risk flag: the collapsed group's flag (any flagged member),
  // else the candidate's own. Styles the row distinctly but stays selectable;
  // the reason shows on hover over the chip.
  flagCategory: candidate.aliasGroup?.flagCategory ?? candidate.flagCategory,
  flagRationale: candidate.aliasGroup?.flagRationale ?? candidate.flagRationale,
  flagSeverity: candidate.aliasGroup?.flagSeverity ?? candidate.flagSeverity,
  // The Match Review six-factor breakdown, rendered by the shared
  // ConfidenceBreakdown drilldown in the candidate table.
  confidenceFactors: candidate.confidenceFactors,
  addresses: candidate.addresses,
  titleWorkspaces: candidate.titleWorkspaces,
  interpretationCount: candidate.interpretationCount,
  // Address-confidence flag (#26): kept out of `flagged` so the row stays
  // selectable — only submission is blocked, surfaced in Entity Consolidation.
  addressFlagged: candidate.addressFlagged,
  addressFlagCriticality: candidate.addressFlagCriticality,
});

type AliasGroupSummary = NonNullable<
  EntityReconcileScoredResponse['viewedAliasGroup']
>;
export type AliasGroupExtras = Map<number, AliasGroupSummary>;

/**
 * Alias-family data keyed by the id the model uses (an alias-group candidate is
 * keyed by its display member's id), threaded off the raw scored payload that
 * scoredCandidate flattens away. The anchor's own family is excluded from the
 * scored list, so it is keyed by the anchor's id.
 *
 * @param candidates - The raw scored candidates.
 * @param anchor - The viewed/anchor entity and its own alias family, if any.
 * @return Alias-family member counts and types, keyed by candidate id.
 */
export const aliasGroupExtrasFor = (
  candidates: EntityReconcileCandidate[],
  anchor?: { id: number; aliasGroup: AliasGroupSummary | null | undefined }
): AliasGroupExtras => {
  const extras: AliasGroupExtras = new Map();
  candidates.forEach((c) => {
    if (c.aliasGroup) {
      extras.set(candidateRowId(c), {
        memberCount: c.aliasGroup.memberCount,
        memberTypes: c.aliasGroup.memberTypes,
      });
    }
  });
  if (anchor?.aliasGroup) extras.set(anchor.id, anchor.aliasGroup);
  return extras;
};

/**
 * A candidate row as the Entity Consolidation model sees it.
 *
 * @param c - The candidate row.
 * @param extras - Alias-family data keyed by candidate id.
 * @return The candidate in the consolidation model's shape.
 */
export const consolidationCandidate = (
  c: ReconcileCandidate,
  extras: AliasGroupExtras
): ConsolidationCandidate => {
  const extra = extras.get(c.id);
  return {
    id: c.id,
    name: c.name,
    type: c.type,
    confidence: c.confidence,
    addressFlagged: c.addressFlagged,
    aliasGroupMemberCount: extra?.memberCount,
    // The alias family's type breakdown (§3.7 Alias Group column).
    aliasGroupMemberTypes: extra?.memberTypes,
    // Entity::ParticipationCounts gives the distinct workspace LIST
    // (titleWorkspaces); its length is the count the name-level sums roll up.
    workspaceCount: c.titleWorkspaces?.length ?? 0,
    interpretationCount: c.interpretationCount ?? 0,
    userAdded: c.sourceLabel?.startsWith('User Added') ?? false,
    // Pins the anchor name to the top of the right panel (§ sort).
    staged: c.isStaged,
  };
};
